// src/app/kartDetayUrl.ts
//
// ─── KART DETAY MODALI → ADRES ÇUBUĞU (saf karar katmanı) ─────────────────────
//
// ── NEDEN VAR (ölçüm, varsayım değil) ────────────────────────────────────────
// Profil MyCards ızgarasında/vitrininde bir karta dokununca açılan detay modalı
// SALT YEREL state'ti:
//   ProfileMobile.tsx  `onOpenCard={(c) => setLastDrop({ ...c, _reveal: false })}`
//   ProfileDesktop.tsx aynısı — ve iki dosyada da modal için `pushState` YOK.
// Üç somut kayıp (haritanın `/place/{id}` boşluğunun birebir eşi):
//   1. Açık kart PAYLAŞILAMIYOR (URL'de iz yok).
//   2. Yenileyince kart KAYBOLUYOR (cold-load'da geri getirecek bilgi yok).
//   3. Geri tuşu modalı kapatmıyor — kullanıcıyı PROFİLDEN ÇIKARIYOR: modalın
//      kendi history girdisi olmadığı için pop, ALTINDAKİ girdiyi tüketiyor.
//      Yani yanlış katman poplanıyor.
//
// 🧭 KANON (CLAUDE.md "URL / deep-link parite kontrolü"): her görünüm kendi
//    URL'sini üretmeli, cold-load ve paylaşım çalışmalı.
//
// ── ŞEMA: YENİ ROTA DEĞİL, MEVCUT ALT-YOLUN BİR SEGMENT DERİNİ ────────────────
//   `/{kullanici}/cards/{kod}`  ·  eski `/u/{kullanici}/cards/{kod}` de kabul.
// `/{u}/highlights/{id}` ile birebir aynı biçim. Bu yüzden YENİ REZERVE KELİME
// GEREKMEZ (ölçüldü): ilk segment zaten kullanıcı adı, `cards` bir alt-segment.
// `firestore.rules` ile eşitlik isteyen `reservedUsernames.test.ts` bu turda
// DOKUNULMADAN yeşil kalır.
//
// ⚠️ `/card/{kod}` (TEKİL) BAŞKA BİR ROTADIR: sahiplik iddia etmeyen statik
// template sayfası (`CardTemplatePage`). Buradaki çoğul `/cards/{kod}` ise
// BELİRLİ BİR KULLANICININ koleksiyonundaki kartın detayıdır. İkisi karışmasın
// diye `kartDetayYoluMu("/card/x")` bilerek `false` döner (testte kilitli).
//
// ── NİYE AYRI + SAF DOSYA ────────────────────────────────────────────────────
// Karar `ProfileMobile`/`ProfileDesktop` içine gömülseydi (ikisi ~1600 satır,
// Firestore + harita + medya bağımlı) ancak "kaynak metinde şu dizge var mı"
// ile ölçülebilirdi. Burada DOM'a dokunulmaz; `window.history` çağrısını
// `useKartDetayUrl` yapar. Aynı ikili proje kanonunda zaten var:
// `haritaMekanUrl.ts` + `useHaritaMekanUrl.ts` (ve `computeClipModalPush`).
//
// ── PUSH mu REPLACE mi (kural: "katmanlar push, modlar replace") ─────────────
//   · kart YOK → VAR    : **push**    → GERİ = kartı kapat (talebin çekirdeği)
//   · kart A → kart B   : **replace** → her kart dokunuşu history şişirmesin
//   · kart VAR → YOK    : **replace** → `/{u}/cards`'a dön, sahte giriş üretme
//   · aynı kart tekrar  : **yok**     → duplicate-history guard
//     (`computeClipModalPush`/`haritaSecimUrlKarari` guard'ının eşi)

/** `/{u}/cards/{kod}` — eski `/u/` öneki opsiyonel; trailing slash serbest. */
export const KART_DETAY_YOLU_DESENI = /^\/(?:u\/)?([^/]+)\/cards\/([A-Za-z0-9_-]+)\/?$/;

/** Kart kodunun kabul edilen biçimi — desendeki grupla BİREBİR aynı sınıf. */
const KOD_DESENI = /^[A-Za-z0-9_-]+$/;

/** Kart detayının yazdığı history state'i — popstate'te ayırt edilebilsin. */
export type KartUrlDurumu = { __kartDetay: true; kod: string };

export type KartUrlKarari =
  | { tur: "yok"; hedef: null; durum: null }
  | { tur: "push"; hedef: string; durum: KartUrlDurumu }
  | { tur: "replace"; hedef: string; durum: KartUrlDurumu | null };

/** Yol bir kart-detay permalinki mi? (saf yüklem — popstate kolu bunu kullanır) */
export function kartDetayYoluMu(yol?: string | null): boolean {
  return KART_DETAY_YOLU_DESENI.test(yol || "");
}

/**
 * Yol, kart IZGARASININ kendi yolu mu (`/{u}/cards` — detay modalı KAPALI)?
 *
 * ⚠️ NİYE BURADA: `/…/cards…` şemasının tek sahibi bu dosya. Izgara yüklemi
 * `gorunumFiltreUrl.ts`e (MyCards filtre parametresinin yol kapısı) yazılsaydı
 * aynı şemanın İKİNCİ tanımı doğardı ve sessizce ayrışabilirdi — bu depoda
 * ölçülmüş zarar sınıfı (`icerikPermalink.ts` başlığındaki `pathFor` vakası).
 *
 * `kartDetayYoluMu`nun TAMAMLAYICISI, tersi DEĞİL: `/ali` (çıplak profil) ve
 * `/explore` ikisinde de `false` döner.
 */
export function kartIzgaraYolundaMi(yol?: string | null): boolean {
  const segs = (yol || "").split("/").filter(Boolean);
  // `/u/` öneki `kartTabanYolu` ile BİREBİR aynı şekilde atılır.
  const base = segs[0] === "u" ? segs.slice(1) : segs;
  if (base.length !== 2) return false;
  try {
    return decodeURIComponent(base[1]).toLowerCase() === "cards";
  } catch {
    // Bozuk yüzde kodlaması → ham segment (profilSekmeUrl'ün aynı savunması).
    return base[1].toLowerCase() === "cards";
  }
}

/** `/{u}/cards/{kod}` → `{seg1, kod}`; başka yol → `null`. */
export function kartDetayKimligi(
  yol?: string | null,
): { seg1: string; kod: string } | null {
  const m = (yol || "").match(KART_DETAY_YOLU_DESENI);
  return m ? { seg1: decodeURIComponent(m[1]), kod: m[2] } : null;
}

/**
 * Mevcut yoldan profil taban yolunu (`/{u}/cards`) türetir.
 *
 * ⚠️ TEK KAYNAK NİYETİ: taban, çağıranın elindeki bir prop'tan DEĞİL, adres
 * çubuğundan okunur — profil kullanıcı adını ikinci bir yerde tutmak, iki
 * kaynağın sessizce ayrışmasının başlangıç koşuludur (`profileUrlSync`'in
 * `profileBaseSegs()` ile aynı okuma: baştaki `u/` öneki atılır).
 * Profil yolunda değilsek (`/`, `/explore`…) `null` → hiçbir şey yazılmaz.
 */
export function kartTabanYolu(mevcutYol?: string | null): string | null {
  const segs = (mevcutYol || "").split("/").filter(Boolean);
  const base = segs[0] === "u" ? segs.slice(1) : segs;
  const seg1 = base[0];
  if (!seg1) return null;
  // Taban ancak profil yolundayken anlamlı. `/{u}` (ızgara) ve `/{u}/cards`
  // ikisi de geçerli taban; `/{u}/tagged` gibi başka alt-yollarda kart modalı
  // açılamaz (ızgara o sekmede yok) ama yazsak da doğru tabanı üretiriz.
  return `/${seg1}/cards`;
}

/**
 * Detay modalının seçili kartı değişti → adres çubuğuna ne yazılmalı?
 *
 * `kod: unknown` BİLEREK (`computeClipModalPush` / `haritaSecimUrlKarari` ile
 * aynı gerekçe): üretim çağrısı `lastDrop?.code` geçiyor ve o
 * `string | undefined`; modal kapanınca `null` geliyor. Gövde
 * `== null ? "" : String()` ile hepsini karşılıyor.
 */
export function kartSecimUrlKarari(
  kod: unknown,
  mevcutYol?: string | null,
): KartUrlKarari {
  const yol = mevcutYol || "";
  const k = (kod == null ? "" : String(kod)).trim();
  const taban = kartTabanYolu(yol);

  if (!k) {
    // Seçim kalktı. Yalnız BİZİM yazdığımız bir kart yolundaysak geri al;
    // başka bir sayfadaysak adres çubuğuna DOKUNMA.
    if (kartDetayYoluMu(yol) && taban) {
      return { tur: "replace", hedef: taban, durum: null };
    }
    return { tur: "yok", hedef: null, durum: null };
  }

  // Şemaya uymayan kod için URL yazmak, geri dönüşü çözülemeyen bir adres
  // üretirdi (`klipDuzenleAcilisKarari`ın aynı kararı).
  if (!KOD_DESENI.test(k)) return { tur: "yok", hedef: null, durum: null };
  if (!taban) return { tur: "yok", hedef: null, durum: null };

  const hedef = `${taban}/${k}`;
  if (yol === hedef) return { tur: "yok", hedef: null, durum: null };

  const durum: KartUrlDurumu = { __kartDetay: true, kod: k };
  // Karttan karta geçiş bir MOD değişimi (aynı katman) → replace.
  if (kartDetayYoluMu(yol)) return { tur: "replace", hedef, durum };
  // Izgaradan ilk açılış bir KATMAN açılışı → push (geri = modalı kapat).
  return { tur: "push", hedef, durum };
}

/**
 * Mount koşusunda URL YAZILMAZ. Sebep `haritaUrlYazimiAtla` ile birebir aynı:
 * effect mount'ta da koşar ve o an seçim null'dur; yazsaydık cold-load
 * `/{u}/cards/{kod}` URL'sini `/{u}/cards`'a EZERDİK — yani düzeltmeye
 * çalıştığımız şeyi bozardık.
 */
export function kartUrlYazimiAtla(opts: { ilkCalisma: boolean }): boolean {
  return !!opts.ilkCalisma;
}

/**
 * In-UI kapanışta (X / scrim / ESC) `history.back()` çağrılmalı mı?
 *
 * ── NİYE GEREKLİ (ölçülmüş wart) ─────────────────────────────────────────
 * Kapanışı yalnız `replaceState` ile yapmak URL'i doğru gösterir ama BİZİM
 * PUSH'LADIĞIMIZ GİRDİYİ YIĞINDA BIRAKIR. Sonuç: kullanıcı X'e basıp modalı
 * kapatır, sonra GERİ tuşuna basar ve **hiçbir şey olmaz** (aynı yola pop
 * eder). Bu, düzeltmeye çalıştığımız "geri tuşu yanlış katmanı harcıyor"
 * kusurunun sessiz kardeşidir.
 * `klipDuzenleKapanistaPopla` ile birebir aynı sözleşme: yalnız KENDİ
 * girdimiz duruyorsa pop'la — çift `back()` alttaki profil katmanını da
 * tüketirdi (kullanıcı feed'e düşerdi).
 */
export function kartKapanistaPopla(
  pushlandi: boolean,
  kod: unknown,
  yol?: string | null,
): boolean {
  const k = (kod == null ? "" : String(kod)).trim();
  // ⚠️ ÜÇÜNCÜ ŞART (`yol`) SAVUNMA AMAÇLI: modal açıkken BAŞKA bir katman
  // history'ye girdi eklerse (bugün kart modalının içinde böyle bir yol YOK —
  // ölçüldü: FairnessSheet/paylaşım `history`ye dokunmuyor) yığının tepesi
  // artık bizim girdimiz olmaz. O durumda `back()` atmak, o katmanı harcardı.
  // Yalnız adres çubuğu hâlâ BİZİ gösteriyorsa pop'la.
  return pushlandi === true && !k && KART_DETAY_YOLU_DESENI.test(yol || "");
}

/**
 * popstate → detay modalı kapansın mı?
 *
 * URL artık bir kart permalinki DEĞİLSE (ör. geri tuşuyla `/{u}/cards`'a
 * dönüldü) modal kapanmalı. Hâlâ `/{u}/cards/{kod}` ise (ileri tuşu) kapatma
 * YAPILMAZ — kanca o kolda modalı yeniden AÇAR.
 */
export function kartSecimPopTemizler(yol?: string | null): boolean {
  return !kartDetayYoluMu(yol);
}

/**
 * SOĞUK AÇILIŞ kararı — `/{u}/cards/{kod}` doğrudan açıldığında.
 *
 * Sorun: o anda history yığınında ALTIMIZDA `/{u}/cards` girdisi YOKTUR;
 * geri tuşu doğrudan önceki siteye/boşluğa gider. Yani "geri = modalı kapat"
 * sözü soğuk açılışta tutulamaz.
 * Çözüm: tabanı `replaceState` ile yaz, hedefi `pushState` ile üstüne koy →
 * yığın sıcak açılıştakiyle AYNI şekle gelir ([taban, hedef]) ve geri tuşu
 * modalı kapatır, profilden ATMAZ.
 */
export function kartSogukAcilisKarari(
  yol?: string | null,
): { kod: string; taban: string; hedef: string } | null {
  const kimlik = kartDetayKimligi(yol);
  if (!kimlik) return null;
  const taban = kartTabanYolu(yol);
  if (!taban) return null;
  return { kod: kimlik.kod, taban, hedef: `${taban}/${kimlik.kod}` };
}
